/**
 * `qf.geometry` — shapes a server-side detector found, decoded off the schema.
 *
 * The server half is `services/qv-data/app/qf/geometry.py`; the design is
 * `docs/plans/chart-patterns.md`. A pattern is not a series, so it never
 * arrives as a column: the chart's own Arrow response carries one JSON value
 * on its schema metadata, beside `qv.today` and `qv.phase`, keyed by the plot
 * alias that asked for it.
 *
 * ## Five primitives, and this package knows only their shape
 *
 * `segment`, `path`, `level`, `label`, `target`, `span`. What a pattern *is* stays on
 * the server; what a primitive *looks like* is the chart engine's. This file is
 * the wire shape between them and nothing else, so a new pattern changes none
 * of it.
 *
 * ## Points are bar times in epoch seconds
 *
 * `t` is in the unit {@link timeValueToSeconds} decodes a time column into, so
 * a point names exactly the bar the candles carry — UTC midnight for a daily
 * bar, the bar's own instant intraday.
 *
 * ## Malformed is absent, never an exception
 *
 * Shapes are the one optional thing on a response that otherwise draws a
 * chart. A version this client does not know, or JSON it cannot read, drops
 * the shapes and keeps the candles.
 */

/** A bar's time (epoch seconds) and a price. */
export interface GeometryPoint {
  t: number;
  p: number;
}

/** Which theme role styles a primitive — support, resistance, outline, neckline, target… */
export type GeometryRole = string;

export interface GeometrySegment {
  t: "segment";
  role: GeometryRole;
  /** Where the line starts. A trendline's first pivot. */
  a: GeometryPoint;
  /** Where it ends: the break bar, or the last bar if it never broke. */
  b: GeometryPoint;
  /** A point it passes through between them — a trendline's second pivot. */
  via?: GeometryPoint;
  /** Price change per *bar* — the unit that keeps an extension on the line across gaps. */
  slopePerBar?: number;
  /** Bars that touched it, epoch seconds. */
  touches?: number[];
}

export interface GeometryPath {
  t: "path";
  role: GeometryRole;
  points: GeometryPoint[];
}

export interface GeometryLevel {
  t: "level";
  role: GeometryRole;
  price: number;
  from: number;
  to?: number;
  /** Cover the bars from edge to edge, not bar time to bar time (a `seasHits` block). */
  edges?: boolean;
  /** Sessions after the last bar, for an end in the future — as {@link GeometrySpan}'s. */
  aheadFrom?: number;
  aheadTo?: number;
}

export interface GeometryLabel {
  t: "label";
  role?: GeometryRole;
  at: GeometryPoint;
  text: string;
}

export interface GeometryTarget {
  t: "target";
  role?: GeometryRole;
  from: GeometryPoint;
  price: number;
  /** How many bars past `from` the target line reaches. Absent: the client's default. */
  bars?: number;
}

/**
 * A stretch of time, drawn as a full-height background (`seasBands`). `from` and
 * `to` are session times; an end past the last bar also says how many sessions
 * after it (`aheadFrom` / `aheadTo`), because the chart places the future by bar
 * count and a calendar date there would land between bars.
 */
export interface GeometrySpan {
  t: "span";
  role: GeometryRole;
  from: number;
  to: number;
  aheadFrom?: number;
  aheadTo?: number;
}

export type GeometryPrimitive =
  | GeometrySegment
  | GeometryPath
  | GeometryLevel
  | GeometryLabel
  | GeometryTarget
  | GeometrySpan;

/** What today's close is checked against, live, to end a line or confirm a pattern. */
export interface GeometryTrigger {
  /** The bar `price` is measured at — the last bar the server saw. */
  t: number;
  price: number;
  slopePerBar: number;
  /** A close past `price + slopePerBar × bars since t` in this direction fires it. */
  dir: "below" | "above";
}

/**
 * A pattern's facts for Bar Intelligence (`docs/plans/bar-intelligence.md`):
 * where it starts and ends, how many bars, and the prices it is measured by.
 * The client does every measurement; nothing here is relative to a bar.
 */
export interface GeometryStats {
  start: number;
  end: number;
  bars: number;
  top?: number;
  bottom?: number;
  trigger?: number;
  target?: number;
  stop?: number;
  /** A cup's handle, measured the same way. */
  handle?: GeometryStats;
}

/** Settled daily bars a non-daily chart is sent for Bar Intelligence — columns, epoch seconds. */
export interface GeometryDaily {
  t: number[];
  h: number[];
  l: number[];
  c: number[];
}

/** The all-time extremes older than {@link GeometryDaily}, and how many bars before it. */
export interface GeometrySeeds {
  ath?: { p: number; t: number; before: number };
  atl?: { p: number; t: number; before: number };
}

/** One `seasHits` block: its years up and down, beat-their-own-year count, and the % moves summed and averaged. */
export interface GeometryHits {
  up: number;
  down: number;
  years: number;
  xs: number;
  sumPos: number;
  sumNeg: number;
  sumDelta: number;
  avgWin: number;
  avgLoss: number;
  avgDelta: number;
}

export interface GeometryShape {
  /** Stable across responses while the shape is the same — a key for diffing. */
  id: string;
  kind: string;
  bias?: "bullish" | "bearish";
  /**
   * A trendline is `active` or `broken`; a pattern is `forming`, `confirmed`
   * or `failed`. Kept as a string: the vocabulary is per kind and the engine
   * styles by `(kind, status)`.
   */
  status: string;
  /** A trendline's place in the screener's ranking — `< 10` is the screener's set. */
  rank?: number;
  trigger?: GeometryTrigger;
  /** When a broken line broke, epoch seconds. */
  breakAt?: number;
  primitives: GeometryPrimitive[];
  /** A pattern's facts — {@link GeometryStats}. */
  stats?: GeometryStats;
  /** `seasHits` only: the block's past years, counted — the legend's reading. */
  hits?: GeometryHits;
  /** Bar Intelligence's `dailyContext` shape only. */
  daily?: GeometryDaily;
  seeds?: GeometrySeeds;
}

export interface GeometryPayload {
  v: 1;
  /** The last real bar the detectors saw, epoch seconds. */
  asOf: number;
  /** Shapes per plot alias — two pattern plots on one chart each find their own. */
  sets: Record<string, GeometryShape[]>;
}

/** The schema-metadata key. */
export const GEOMETRY_METADATA_KEY = "qf.geometry";

/**
 * The payload, or `undefined` when there is none or it cannot be trusted.
 *
 * Checks the envelope and each shape's outline — enough that a consumer can
 * iterate without guarding every field — and drops a shape whose primitives
 * are not an array rather than the whole set.
 */
export function parseGeometry(raw: string | null | undefined): GeometryPayload | undefined {
  if (!raw) return undefined;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof value !== "object" || value === null) return undefined;
  const { v, asOf, sets } = value as { v?: unknown; asOf?: unknown; sets?: unknown };
  if (v !== 1 || typeof asOf !== "number" || typeof sets !== "object" || sets === null) {
    return undefined;
  }
  const clean: Record<string, GeometryShape[]> = {};
  for (const [alias, shapes] of Object.entries(sets as Record<string, unknown>)) {
    if (!Array.isArray(shapes)) continue;
    clean[alias] = shapes.filter(
      (s): s is GeometryShape =>
        typeof s === "object" &&
        s !== null &&
        typeof (s as GeometryShape).id === "string" &&
        typeof (s as GeometryShape).kind === "string" &&
        Array.isArray((s as GeometryShape).primitives),
    );
  }
  return { v: 1, asOf, sets: clean };
}
